import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { User } from '../types';
import { MENU_REGISTRY, ALL_MENU_KEYS, PlatformId } from '../config/menuRegistry';

interface UserMenuPermissionsModalProps {
  user: User;
  onClose: () => void;
}

const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) {
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return fetch(url, { ...options, headers });
};

export function UserMenuPermissionsModal({ user, onClose }: UserMenuPermissionsModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fullAccess, setFullAccess] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set(ALL_MENU_KEYS));
  const [primaryPlatform, setPrimaryPlatform] = useState<PlatformId>('crm');
  // Agendamento de reuniões pelo Chat CRM (por usuário)
  const [meetingCanSchedule, setMeetingCanSchedule] = useState(true);
  const [meetingForOthers, setMeetingForOthers] = useState(false);
  const [meetingAllowAll, setMeetingAllowAll] = useState(true);
  const [meetingAllowed, setMeetingAllowed] = useState<Set<string>>(new Set());
  const [tenantUsers, setTenantUsers] = useState<{ id: string; nome: string; email: string }[]>([]);
  const availablePlatforms = useMemo(
    () => MENU_REGISTRY.filter(platform => fullAccess || platform.menus.some(menu => selected.has(menu.key))),
    [fullAccess, selected]
  );

  useEffect(() => {
    if (availablePlatforms.length > 0 && !availablePlatforms.some(platform => platform.id === primaryPlatform)) {
      setPrimaryPlatform(availablePlatforms[0].id);
    }
  }, [availablePlatforms, primaryPlatform]);

  useEffect(() => {
    (async () => {
      try {
        const response = await authenticatedFetch(`/api/menu-permissions/users/${user.id}`);
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.message || 'Erro ao carregar permissões');
        }
        const data = await response.json();
        setPrimaryPlatform(data.primaryPlatform || 'crm');
        // Permissões de reunião + lista de usuários do tenant (para "pode agendar para quem")
        try {
          const [mp, fu] = await Promise.all([
            authenticatedFetch(`/api/meetings/permissions/users/${user.id}`).then(r => r.json()),
            authenticatedFetch('/api/flow/users').then(r => r.json())
          ]);
          const perms = mp?.meetingPermissions || {};
          setMeetingCanSchedule(perms.canSchedule !== false);
          setMeetingForOthers(perms.forOthers === true);
          setMeetingAllowAll(!Array.isArray(perms.allowedUserIds));
          setMeetingAllowed(new Set(Array.isArray(perms.allowedUserIds) ? perms.allowedUserIds : []));
          setTenantUsers((fu?.users || []).filter((u: any) => u.id !== user.id));
        } catch {
          // sem bloquear o modal de menus
        }
        if (Array.isArray(data.menuPermissions)) {
          setFullAccess(false);
          setSelected(new Set(data.menuPermissions));
        } else {
          setFullAccess(true);
          setSelected(new Set(ALL_MENU_KEYS));
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Erro ao carregar permissões');
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [user.id]);

  const toggleKey = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const togglePlatform = (platformKeys: string[], allChecked: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      platformKeys.forEach(k => (allChecked ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const handleSave = async () => {
    if (availablePlatforms.length === 0) {
      toast.error('Libere ao menos uma plataforma para definir a plataforma principal.');
      return;
    }
    setSaving(true);
    try {
      const response = await authenticatedFetch(`/api/menu-permissions/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          menuPermissions: fullAccess ? null : Array.from(selected),
          primaryPlatform,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Erro ao salvar permissões');
      }
      // Permissões de reunião (independentes dos menus)
      const meetingResponse = await authenticatedFetch(`/api/meetings/permissions/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ meetingPermissions: { canSchedule: meetingCanSchedule, forOthers: meetingForOthers, allowedUserIds: meetingForOthers && !meetingAllowAll ? Array.from(meetingAllowed) : null } }),
      });
      if (!meetingResponse.ok) {
        const data = await meetingResponse.json().catch(() => ({}));
        throw new Error(data.message || 'Erro ao salvar permissões de reunião');
      }
      toast.success('Permissões atualizadas com sucesso!');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar permissões');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-bold text-gray-900">Permissões de menu</h2>
          <p className="text-sm text-gray-500">
            Defina quais menus <span className="font-semibold">{user.nome}</span> pode acessar em cada plataforma.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <label className="mb-4 flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
              <input
                type="checkbox"
                checked={fullAccess}
                onChange={e => setFullAccess(e.target.checked)}
                className="h-4 w-4 accent-blue-600"
              />
              <span className="text-sm font-semibold text-blue-900">
                Acesso total (todos os menus de todas as plataformas)
              </span>
            </label>

            <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <label htmlFor="primary-platform" className="mb-1 block text-sm font-semibold text-emerald-950">
                Plataforma principal
              </label>
              <p className="mb-2 text-xs text-emerald-800">
                Esta será a primeira plataforma aberta quando o usuário entrar no sistema.
              </p>
              <select
                id="primary-platform"
                value={primaryPlatform}
                onChange={event => setPrimaryPlatform(event.target.value as PlatformId)}
                className="w-full rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
              >
                {availablePlatforms.map(platform => (
                    <option key={platform.id} value={platform.id}>{platform.label}</option>
                  ))}
              </select>
            </div>

            <div className={fullAccess ? 'pointer-events-none opacity-40' : ''}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {MENU_REGISTRY.map(platform => {
                  const keys = platform.menus.map(m => m.key);
                  const allChecked = keys.every(k => selected.has(k));
                  return (
                    <div key={platform.id} className="rounded-xl border border-gray-200 p-4">
                      <label className="mb-2 flex items-center gap-2 border-b border-gray-100 pb-2">
                        <input
                          type="checkbox"
                          checked={allChecked}
                          onChange={() => togglePlatform(keys, allChecked)}
                          className="h-4 w-4 accent-blue-600"
                        />
                        <span className="text-sm font-bold text-gray-900">{platform.label}</span>
                      </label>
                      <div className="space-y-1.5">
                        {platform.menus.map(menu => (
                          <label key={menu.key} className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={selected.has(menu.key)}
                              onChange={() => toggleKey(menu.key)}
                              className="h-4 w-4 accent-blue-600"
                            />
                            {menu.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-gray-400">
                Plataformas sem nenhum menu marcado ficam ocultas para o usuário.
              </p>
            </div>

            {/* Agendamento de reuniões (Chat CRM -> agenda do Flow) */}
            <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/50 p-4">
              <p className="text-sm font-bold text-gray-900">📅 Agendamento de reuniões (Chat CRM)</p>
              <p className="mb-3 text-xs text-gray-600">Define se {user.nome} pode marcar reuniões com contatos pelo botão "Agendar Reunião" do chat, e em quais agendas do Flow.</p>
              <label className="flex items-center gap-2 text-sm text-gray-800">
                <input type="checkbox" checked={meetingCanSchedule} onChange={e => setMeetingCanSchedule(e.target.checked)} className="h-4 w-4 accent-amber-600" />
                Pode agendar reuniões
              </label>
              {meetingCanSchedule && (
                <div className="mt-2 space-y-2 pl-6">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="radio" name="meeting-scope" checked={!meetingForOthers} onChange={() => setMeetingForOthers(false)} className="h-4 w-4 accent-amber-600" />
                    Apenas na própria agenda
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="radio" name="meeting-scope" checked={meetingForOthers} onChange={() => setMeetingForOthers(true)} className="h-4 w-4 accent-amber-600" />
                    Pode agendar para outras pessoas
                  </label>
                  {meetingForOthers && (
                    <div className="ml-6 rounded-lg border border-amber-200 bg-white p-3">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={meetingAllowAll} onChange={e => setMeetingAllowAll(e.target.checked)} className="h-4 w-4 accent-amber-600" />
                        Para qualquer usuário da empresa
                      </label>
                      {!meetingAllowAll && (
                        <div className="mt-2 max-h-40 space-y-1 overflow-y-auto border-t border-gray-100 pt-2">
                          {tenantUsers.length === 0 && <p className="text-xs text-gray-400">Nenhum outro usuário encontrado.</p>}
                          {tenantUsers.map(u => (
                            <label key={u.id} className="flex items-center gap-2 text-sm text-gray-700">
                              <input type="checkbox" checked={meetingAllowed.has(u.id)} onChange={() => setMeetingAllowed(prev => { const n = new Set(prev); if (n.has(u.id)) n.delete(u.id); else n.add(u.id); return n; })} className="h-4 w-4 accent-amber-600" />
                              {u.nome} <span className="text-xs text-gray-400">{u.email}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={loading || saving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Salvar permissões'}
          </button>
        </div>
      </div>
    </div>
  );
}
